import { Component, computed, inject, signal, viewChild, ElementRef } from '@angular/core';
import {
  Button,
  Skeleton,
  Alert,
  ConfirmService,
  ToggleLabel,
  Toggle,
  NotificationService,
} from 'excellence-ui';
import { NgIcon } from '@ng-icons/core';
import { SupplierContractChecksStore } from '../../stores/supplier-contract-checks.store';
import { Router } from '@angular/router';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { SupplierContractsDetailsPage } from '../supplier-contracts-details-page/supplier-contracts-details-page';
import { SupplierContractCheckSkeleton } from '../../components/supplier-contract-check-skeleton/supplier-contract-check-skeleton';
import { HttpErrorComponent } from '../../../../shared/components/http-error/http-error';
import { SupplierContractCheckEditForm } from '../../components/supplier-contract-check-edit-form/supplier-contract-check-edit-form';
import { SupplierContractCheck } from '../../components/supplier-contract-check/supplier-contract-check';
import { SupplierContractCheckFormModel } from '../../components/supplier-contract-check-edit-form/definitions';
import { form, FormField } from '@angular/forms/signals';
import { HasRoleDirective } from '../../../../shared/directives/has-role';
import { NotHasRoleDirective } from '../../../../shared/directives/not-has-role';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-supplier-contract-details-check-page',
  imports: [
    Button,
    NgIcon,
    Skeleton,
    ErrorPage,
    SupplierContractCheckSkeleton,
    HttpErrorComponent,
    Alert,
    SupplierContractCheckEditForm,
    SupplierContractCheck,
    ToggleLabel,
    Toggle,
    FormField,
    HasRoleDirective,
    NotHasRoleDirective,
    TranslocoPipe,
  ],
  templateUrl: './supplier-contract-details-check-page.html',
})
export class SupplierContractDetailsCheckPage {
  private supplierContractChecksStore = inject(SupplierContractChecksStore);
  protected router = inject(Router);
  private confirmService = inject(ConfirmService);
  private notificationService = inject(NotificationService);
  private parent = inject(SupplierContractsDetailsPage);
  private formComponent = viewChild(SupplierContractCheckEditForm);
  private elementRef = inject(ElementRef);
  private translocoService = inject(TranslocoService);

  protected organizationId = this.parent.organizationId;
  private supplierContractId = this.parent.supplierContractId;

  protected allLoading = this.supplierContractChecksStore.isLoading;
  protected error = this.supplierContractChecksStore.error;
  protected checks = this.supplierContractChecksStore.filteredSupplierContractChecks;
  protected latestCheck = computed(() => this.checks()?.[0]);
  protected status = computed(() => this.latestCheck()?.data?.status);

  protected isCreateLoading = this.supplierContractChecksStore.isCreateLoading;
  protected createError = this.supplierContractChecksStore.createError;

  protected isUpdateLoading = computed(() => !!this.latestCheck()?.isUpdateLoading);
  protected updateError = computed(() => this.latestCheck()?.updateError);

  protected isDeleteLoading = computed(() => !!this.latestCheck()?.isDeleteLoading);
  protected deleteError = computed(() => this.latestCheck()?.deleteError);

  protected isPublishLoading = computed(() => !!this.latestCheck()?.isPublishLoading);
  protected publishError = computed(() => this.latestCheck()?.publishError);

  protected viewSettings = signal({
    showPreview: false,
  });
  protected viewSettingsForm = form(this.viewSettings);

  protected onClickCreateCheck() {
    this.supplierContractChecksStore.create();
  }

  protected async onClickPublish() {
    this.viewSettings.update((v) => ({
      ...v,
      showPreview: false,
    }));
    await new Promise((resolve) => setTimeout(resolve));
    const formComponent = this.formComponent();
    if (!formComponent) {
      throw new Error('Form component not found');
    }
    if (!formComponent.form().valid()) {
      formComponent.markAsTouched();
      this.notificationService.showError({
        title: this.translocoService.translate(
          'cockpit.supplierContracts.check.validationErrorTitle',
        ),
        subtitle: this.translocoService.translate(
          'cockpit.supplierContracts.check.validationErrorSubtitle',
        ),
      });
      this.scrollToFirstError();
      return;
    }
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.publishConfirmCancel',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.publishConfirmConfirm',
        ),
        title: this.translocoService.translate(
          'cockpit.supplierContracts.check.publishConfirmTitle',
        ),
        message: this.translocoService.translate(
          'cockpit.supplierContracts.check.publishConfirmMessage',
        ),
        icon: 'heroPaperAirplane',
      }))
    ) {
      return;
    }
    this.supplierContractChecksStore.publish(this.latestCheck()!.id);
  }

  protected async onClickReopen() {
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.reopenConfirmCancel',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.reopenConfirmConfirm',
        ),
        title: this.translocoService.translate(
          'cockpit.supplierContracts.check.reopenConfirmTitle',
        ),
        message: this.translocoService.translate(
          'cockpit.supplierContracts.check.reopenConfirmMessage',
        ),
      }))
    ) {
      return;
    }
    this.supplierContractChecksStore.reopen(this.latestCheck()!.id);
  }

  protected async onClickDelete(id: string) {
    if (
      !(await this.confirmService.confirm({
        allowCancel: true,
        cancelButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.deleteConfirmCancel',
        ),
        confirmButtonText: this.translocoService.translate(
          'cockpit.supplierContracts.check.deleteConfirmConfirm',
        ),
        title: this.translocoService.translate(
          'cockpit.supplierContracts.check.deleteConfirmTitle',
        ),
        message: this.translocoService.translate(
          'cockpit.supplierContracts.check.deleteConfirmMessage',
        ),
        mode: 'destructive',
        icon: 'heroTrash',
      }))
    ) {
      return;
    }
    this.supplierContractChecksStore.delete(id);
  }

  protected onChangeFormData(data: SupplierContractCheckFormModel) {
    this.supplierContractChecksStore.update(this.latestCheck()!.id, {
      ...data,
      supplierContractIds: [this.supplierContractId()],
    });
  }

  private scrollToFirstError() {
    setTimeout(() => {
      const firstError = this.elementRef.nativeElement.querySelector('ui-input-error');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }
}
